import { createContext, useContext, useEffect, useRef, useState } from "react";
import supabase from "../supabaseClient";

const AppIssueContext = createContext({});

const AppIssueContextProvider = ({ children }) => {
  let myChannel = null;
  const [username, setUsername] = useState("");
  const [session, setSession] = useState(null);
  const [issues, setIssues] = useState([]);
  const [error, setError] = useState("");
  const [loadingInitial, setLoadingInitial] = useState(true);
  const [routeHash, setRouteHash] = useState("");
  const [isOnBottom, setIsOnBottom] = useState(false);
  const [newIncomingIssueTrigger, setNewIncomingIssueTrigger] = useState(null);
  const [unviewedIssueCount, setUnviewedIssueCount] = useState(0);
  const [countryCode, setCountryCode] = useState("");
  const [isInitialLoad, setIsInitialLoad] = useState(false);

  const scrollRef = useRef();

  const getLocation = async () => {
    try {
      const res = await fetch("https://api.db-ip.com/v2/free/self");
      const { countryCode, error } = await res.json();
      if (error) throw new Error(error);

      setCountryCode(countryCode);
      localStorage.setItem("countryCode", countryCode);
    } catch (error) {
      console.error(`error getting location from api.db-ip.com:`, error.message);
    }
  };

  const randomUsername = () => {
    return `@user${Date.now().toString().slice(-4)}`;
  };
  const initializeUser = (session) => {
    setSession(session);
    // const {
    //   data: { session },
    // } = await supabase.auth.getSession();

    let username;
    if (session) {
      username = session.user.user_metadata.user_name;
    } else {
      username = localStorage.getItem("username") || randomUsername();
    }
    setUsername(username);
    localStorage.setItem("username", username);
  };

  const handleNewIssue = (payload) => {
    setIssues((prevIssues) => [payload.new, ...prevIssues]);
    //* needed to trigger react state because I need access to the username state
    setNewIncomingIssueTrigger(payload.new);
  };

  const getInitialIssues = async () => {
    if (issues.length) return;

    const { data, error } = await supabase.from("issues").select().range(0, 49).order("id", { ascending: false });
    // console.log(`data`, data);

    setLoadingInitial(false);
    if (error) {
      setError(error.message);
      return;
    }

    setIsInitialLoad(true);
    setIssues(data);
    // scrollToBottom(); // not sure why this stopped working, meanwhile using useEffect that's listening to issues and isInitialLoad state.
  };

  const getIssuesAndSubscribe = async () => {
    setError("");

    await getInitialIssues();

    if (!myChannel) {
      // mySubscription = supabase
      // .from("issues")
      // .on("*", (payload) => {
      //   handleNewIssue(payload);
      // })
      // .subscribe();

      myChannel = supabase
        .channel("custom-all-channel")
        .on("postgres_changes", { event: "*", schema: "public", table: "issues" }, (payload) => {
          handleNewIssue(payload);
        })
        .subscribe();
    }
  };

  const onScroll = async ({ target }) => {
    if (target.scrollHeight - target.scrollTop <= target.clientHeight + 1) {
      setUnviewedIssueCount(0);
      setIsOnBottom(true);
    } else {
      setIsOnBottom(false);
    }

    //* Load more issues when reaching top
    if (target.scrollTop === 0) {
      // console.log("issues.length :>> ", issues.length);
      const { data, error } = await supabase
        .from("issues")
        .select()
        .range(issues.length, issues.length + 49)
        .order("id", { ascending: false });
      if (error) {
        setError(error.message);
        return;
      }
      target.scrollTop = 1;
      setIssues((prevIssues) => [...prevIssues, ...data]);
    }
  };

  const scrollToBottom = () => {
    if (!scrollRef.current) return;

    scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  };

  useEffect(() => {
    // Effect to scroll to bottom on initial message load
    if (isInitialLoad) {
      setIsInitialLoad(false);
      scrollToBottom();
    }
  }, [issues]);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      initializeUser(session);
    });

    getIssuesAndSubscribe();

    const storedCountryCode = localStorage.getItem("countryCode");
    if (storedCountryCode && storedCountryCode !== "undefined") setCountryCode(storedCountryCode);
    else getLocation();

    const {
      data: { subscription: authSubscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      console.log("onAuthStateChange", { _event, session });
      initializeUser(session);
    });

    // const { hash, pathname } = window.location;
    // if (hash && pathname === "/") {
    //   console.log("hash", hash);
    //   setRouteHash(hash);
    // }

    return () => {
      // Remove supabase channel subscription by useEffect unmount
      if (myChannel) {
        supabase.removeChannel(myChannel);
      }

      authSubscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!newIncomingIssueTrigger) return;

    if (newIncomingIssueTrigger.username === username) {
      scrollToBottom();
    } else {
      setUnviewedIssueCount((prevCount) => prevCount + 1);
    }
  }, [newIncomingIssueTrigger]);

  return (
    <AppIssueContext.Provider
      value={{
        issues,
        loadingInitial,
        error,
        getIssuesAndSubscribe,
        username,
        setUsername,
        randomUsername,
        routeHash,
        scrollRef,
        onScroll,
        scrollToBottom,
        isOnBottom,
        country: countryCode,
        unviewedIssueCount,
        session,
      }}
    >
      {children}
    </AppIssueContext.Provider>
  );
};

const useAppIssueContext = () => useContext(AppIssueContext);

export { AppIssueContext as default, AppIssueContextProvider, useAppIssueContext };
